// biome-ignore lint/style/useImportType: Component, ChangeDetectorRef, Input, Output, EventEmitter used at runtime
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
// biome-ignore lint/style/useImportType: FormBuilder used at runtime in constructor
import { FormBuilder, FormGroup } from '@angular/forms';
import { TEXT_LIMIT_EXCEEDED_ERROR_MESSAGE } from '@cdw-ae/common-data';
// biome-ignore lint/style/useImportType: ImmediateErrorStateMatcher used at runtime
import { ImmediateErrorStateMatcher } from '@cdw-ae/common-frontend-materials';
import { Subject, takeUntil } from 'rxjs';

import type { FieldUpdate } from './models/field-update';
import { type FieldUpdateConfig, FieldTemplate, UpdateType } from './models/field-update-config';
// biome-ignore lint/style/useImportType: BulkEditValidationService used at runtime in constructor
import { BulkEditValidationService, type DeviceFieldValues, type ValidationResult } from './services/bulk-edit-validation.service';

/**
 * Event emitted when validation state changes
 */
export interface ValidationStateChangeEvent {
  fieldName: string;
  valid: boolean;
  errorMessage?: string;
  affectedDeviceCount?: number;
}

@Component({
    selector: 'lib-cdw-bulk-field-update',
    templateUrl: './cdw-bulk-field-update.component.html',
    styleUrls: ['./cdw-bulk-field-update.component.scss'],
    standalone: false,
})
export class CdwBulkFieldUpdateComponent implements OnInit, AfterViewInit, OnDestroy {
  fieldUpdateForm!: FormGroup;

  /** Custom error state matcher for immediate validation feedback */
  immediateErrorMatcher = new ImmediateErrorStateMatcher();

  private readonly destroy$ = new Subject<void>();

  @Input() fieldUpdateConfig!: FieldUpdateConfig;

  @Input() fieldUpdate!: FieldUpdate;

  /**
   * Device metadata values for Append/Prepend validation
   * Required when the field supports Append/Prepend operations
   */
  @Input() deviceMetadataValues: DeviceFieldValues[] = [];

  @Output() fieldUpdateChange = new EventEmitter();

  /**
   * Emitted when the validation state of this field changes
   */
  @Output() validationStateChange = new EventEmitter<ValidationStateChangeEvent>();

  /** All validation error messages */
  validationErrorMessages: string[] = [];

  /** Whether the current field value has a validation error */
  hasValidationError = false;

  constructor(
    private fb: FormBuilder,
    private changeDetector: ChangeDetectorRef,
    private validationService: BulkEditValidationService
  ) {}

  ngOnInit(): void {
    this.fieldUpdateForm = this.fb.group({
      fieldUpdateType: [undefined],
      fieldUpdateValue: [undefined],
    });
  }

  ngAfterViewInit(): void {
    this.fieldUpdateForm?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((changes: { fieldUpdateType: string; fieldUpdateValue: unknown }) => {
        const fieldUpdate: FieldUpdate = {
          name: this.fieldUpdateConfig.name,
          updateType: changes.fieldUpdateType,
          value: changes.fieldUpdateValue,
        };
        if (
          !this.fieldUpdateConfig.updateTypes.find((updateType) => updateType.value === changes.fieldUpdateType)
            ?.shouldDisplayTemplate === true
        ) {
          fieldUpdate.value = undefined;
        }

        this.fieldUpdate = fieldUpdate;
        this.validateField();
        this.checkFormValidity();
        this.fieldUpdateChange.emit(fieldUpdate);
      });

    this.fieldUpdateForm.get('fieldUpdateType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((fieldUpdateType: string) => {
        if (
          !this.fieldUpdateConfig.updateTypes.find((updateType) => updateType.value === fieldUpdateType)
            ?.shouldDisplayTemplate === true
        ) {
          this.fieldUpdateForm.get('fieldUpdateValue')?.reset();
        }
        // Re-validate when update type changes (e.g., from Replace to Append)
        this.validateField();
      });

    this.fieldUpdateForm?.get('fieldUpdateType')?.setValue(this.fieldUpdateConfig.updateTypes[0].value);
    this.changeDetector.detectChanges();
    this.checkFormValidity();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showInputTemplate() {
    const selectedUpdateType = this.getSelectedUpdateType();
    if (selectedUpdateType?.shouldDisplayTemplate) {
      return true;
    }
    return false;
  }

  checkFormValidity() {
    if (this.fieldUpdateForm) {
      const selectedType = this.getSelectedUpdateType();
      if (selectedType?.shouldDisplayTemplate) {
        const updateType = this.fieldUpdateForm?.get('fieldUpdateType')?.value as UpdateType;
        const value = this.fieldUpdateForm?.get('fieldUpdateValue')?.value;

        // For Append/Prepend, require a value — empty would clear existing data.
        // For tree-based pickers (e.g. OU), Replace also requires a selection.
        const requiresValue =
          updateType === UpdateType.APPEND ||
          updateType === UpdateType.PREPEND ||
          (updateType === UpdateType.REPLACE && this.fieldUpdateConfig.template === FieldTemplate.TREE);
        if (!value && requiresValue) {
          this.fieldUpdateForm.setErrors({ updateValueRequired: true });
        } else if (this.hasValidationError) {
          // Include validation errors in form validity
          this.fieldUpdateForm.setErrors({ validationError: true });
        } else {
          this.fieldUpdateForm.setErrors(null);
        }
      }
    }
  }

  resetform() {
    this.fieldUpdateForm?.get('fieldUpdateType')?.setValue(this.fieldUpdateConfig.updateTypes[0].value);
    this.fieldUpdateForm?.get('fieldUpdateValue')?.reset();
    this.validationErrorMessages = [];
    this.hasValidationError = false;
  }

  /**
   * Set default values for this field update form
   * @param updateType The update type to set (e.g., UpdateType.REPLACE)
   * @param value The value to set
   */
  setDefaultValues(updateType: string, value: unknown) {
    this.fieldUpdateForm?.get('fieldUpdateType')?.setValue(updateType);
    this.fieldUpdateForm?.get('fieldUpdateValue')?.setValue(value);
    this.changeDetector.detectChanges();
  }

  /**
   * Validate the current field value
   * Runs validation based on the update type (Replace vs Append/Prepend)
   */
  validateField(): void {
    const validation = this.fieldUpdateConfig.validation;
    if (!validation) {
      this.hasValidationError = false;
      this.validationErrorMessages = [];
      return;
    }

    const value = this.fieldUpdateForm?.get('fieldUpdateValue')?.value;
    const updateType = this.fieldUpdateForm?.get('fieldUpdateType')?.value as UpdateType;
    const fieldName = String(this.fieldUpdateConfig.name.value);

    // Skip validation if no value or if update type doesn't require a value
    if (!(value && this.showInputTemplate())) {
      this.hasValidationError = false;
      this.validationErrorMessages = [];
      this.fieldUpdateForm?.get('fieldUpdateValue')?.setErrors(null);
      this.emitValidationState(true);
      return;
    }

    let result: ValidationResult;

    if (updateType === UpdateType.APPEND || updateType === UpdateType.PREPEND) {
      // For Append/Prepend, validate against device values
      result = this.validationService.validateAppendPrependValue(
        fieldName,
        String(value),
        updateType,
        this.deviceMetadataValues
      );
    } else {
      // For Replace, just validate the value itself
      result = this.validationService.validateReplaceValue(fieldName, String(value));
    }

    this.hasValidationError = !result.valid;
    this.validationErrorMessages = result.errorMessages || (result.errorMessage ? [result.errorMessage] : []);

    // Set error on the form control so ErrorStateMatcher can detect it
    const fieldUpdateValueControl = this.fieldUpdateForm?.get('fieldUpdateValue');
    if (result.valid) {
      fieldUpdateValueControl?.setErrors(null);
    } else {
      fieldUpdateValueControl?.setErrors({ validationError: true });
    }

    this.emitValidationState(result.valid, result.errorMessage, result.affectedDeviceCount);

    // Update form validity after validation
    this.checkFormValidity();
  }

  /**
   * Get the current input length for the character counter
   */
  get currentInputLength(): number {
    const value = this.fieldUpdateForm?.get('fieldUpdateValue')?.value;
    return value ? String(value).length : 0;
  }

  /**
   * Get the maximum allowed length for the current field
   * For Append/Prepend, this is the minimum available space across devices
   */
  get maxAllowedLength(): number | undefined {
    const validation = this.fieldUpdateConfig.validation;
    if (!validation?.maxLength) {
      return undefined;
    }

    const updateType = this.fieldUpdateForm?.get('fieldUpdateType')?.value as UpdateType;
    const fieldName = String(this.fieldUpdateConfig.name.value);

    return this.validationService.getMaxAllowedInputLength(
      fieldName,
      this.deviceMetadataValues,
      updateType
    );
  }

  /**
   * Check if this field is a text template that needs validation UI
   */
  get isTextTemplate(): boolean {
    return this.fieldUpdateConfig.template === FieldTemplate.TEXT;
  }

  /**
   * Check if character counter should be shown
   */
  get showCharacterCounter(): boolean {
    return this.isTextTemplate && this.fieldUpdateConfig.validation?.maxLength !== undefined;
  }

  /**
   * Get the character counter label based on update type
   */
  getCharacterCounterLabel(): string {
    const updateType = this.fieldUpdateForm?.get('fieldUpdateType')?.value as UpdateType;
    if (updateType === UpdateType.APPEND || updateType === UpdateType.PREPEND) {
      return `Field limit ${this.maxAllowedLength} characters`;
    }
    return `Max ${this.maxAllowedLength} characters`;
  }

  /**
   * Check if the character limit is exceeded
   */
  get isLimitExceeded(): boolean {
    const maxLength = this.maxAllowedLength;
    return maxLength !== undefined && this.currentInputLength > maxLength;
  }

  /**
   * Get the text limit exceeded error message from the service.
   * This returns the service's message which includes device count for Append/Prepend operations,
   * or the generic message for Replace operations.
   */
  get limitExceededMessage(): string {
    // Find any limit-related error message (could be generic or per-device count)
    const limitMessage = this.validationErrorMessages.find(msg =>
      msg === TEXT_LIMIT_EXCEEDED_ERROR_MESSAGE ||
      msg.includes('Text limit would be exceeded')
    );
    return limitMessage || TEXT_LIMIT_EXCEEDED_ERROR_MESSAGE;
  }

  /**
   * Check if there's a limit exceeded error (either generic or per-device)
   */
  get hasLimitExceededError(): boolean {
    return this.validationErrorMessages.some(msg =>
      msg === TEXT_LIMIT_EXCEEDED_ERROR_MESSAGE ||
      msg.includes('Text limit would be exceeded')
    );
  }

  /**
   * Get error messages excluding text limit exceeded messages
   * (since we show those via the dedicated limit exceeded section instead)
   */
  get nonLimitErrorMessages(): string[] {
    return this.validationErrorMessages.filter(msg =>
      msg !== TEXT_LIMIT_EXCEEDED_ERROR_MESSAGE &&
      !msg.includes('Text limit would be exceeded')
    );
  }

  get hasNonLimitErrors(): boolean {
    return this.nonLimitErrorMessages.length > 0;
  }

  /**
   * Build the textarea's aria-describedby from only the error regions that are
   * actually rendered, so it never references a missing element. The limit-exceeded
   * message lives in the counter row (its own live region); non-limit errors live
   * in the alert container below.
   */
  get ariaDescribedByIds(): string | null {
    const fieldName = this.fieldUpdateConfig.name.value;
    const ids: string[] = [];
    if (this.hasLimitExceededError) {
      ids.push(`bulk-field-limit-${fieldName}`);
    }
    if (this.hasNonLimitErrors) {
      ids.push(`bulk-field-errors-${fieldName}`);
    }
    return ids.length > 0 ? ids.join(' ') : null;
  }

  private getSelectedUpdateType() {
    return this.fieldUpdateConfig.updateTypes.find(
      (updateType) => this.fieldUpdateForm?.get('fieldUpdateType')?.value === updateType.value
    );
  }

  private emitValidationState(valid: boolean, errorMessage?: string, affectedDeviceCount?: number): void {
    this.validationStateChange.emit({
      fieldName: String(this.fieldUpdateConfig.name.value),
      valid,
      errorMessage,
      affectedDeviceCount,
    });
  }
}
